import React, { useState, useEffect, useRef } from "react";
import { Card, Button, Form, InputGroup } from "react-bootstrap";
import { MessageCircle, X, Send, Cpu } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiChat } from "../api/client";
import { useTranslation } from "../hooks/useTranslation";

export default function LessonChatWidget({ lessonContext }) {
  const { t, locale } = useTranslation();
  
  const initialMessages = {
    de: "Hallo! Ich bin dein fintogo Lerncoach. Frag mich alles zur aktuellen Lektion!",
    en: "Hello! I'm your fintogo learning coach. Ask me anything about the current lesson!"
  };
  
  const [isOpen, setIsOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { sender: "ai", text: initialMessages[locale] }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Reset chat when lesson changes or language changes
  useEffect(() => {
    setChatMessages([
      { sender: "ai", text: initialMessages[locale] }
    ]);
  }, [lessonContext?.lessonId, locale]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setChatMessages(prev => [...prev, { sender: "user", text: userMessage }]);
    setInput("");
    setLoading(true);
    setChatMessages(prev => [...prev, { sender: "ai", text: "" }]);

    const systemPrompts = {
      de: `Du bist der fintogo Lerncoach, ein hilfreicher Assistent für Rechnungswesen in Österreich.
Du hilfst Schülern bei Fragen zur aktuellen Lektion.

AKTUELLE LEKTION:
Titel: ${lessonContext?.title || "Unbekannt"}
Modul: ${lessonContext?.moduleTitle || "Unbekannt"}
${lessonContext?.description ? `Beschreibung: ${lessonContext.description}` : ""}
${lessonContext?.content ? `\nInhalt der Lektion:\n${lessonContext.content}` : ""}

Beantworte Fragen basierend auf diesem Kontext. Halte Antworten klar und verständlich.`,
      en: `You are the fintogo learning coach, a helpful assistant for accounting in Austria.
You help students with questions about the current lesson.

CURRENT LESSON:
Title: ${lessonContext?.title || "Unknown"}
Module: ${lessonContext?.moduleTitle || "Unknown"}
${lessonContext?.description ? `Description: ${lessonContext.description}` : ""}
${lessonContext?.content ? `\nLesson content:\n${lessonContext.content}` : ""}

Answer questions based on this context. Keep answers clear and understandable.`
    };
    const systemPrompt = systemPrompts[locale];

    try {
      const messages = [
        ...chatMessages.filter(m => m.text).map(m => ({
          role: m.sender === "user" ? "user" : "assistant",
          content: m.text,
        })),
        { role: "user", content: userMessage }
      ];

      const response = await aiChat(messages, systemPrompt, 1500, true);

      if (!response) {
        throw new Error("No response body");
      }

      const reader = response.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;

              if (content) {
                accumulatedText += content;
                setChatMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1] = { sender: "ai", text: accumulatedText };
                  return newMessages;
                });
              }
            } catch (e) {}
          }
        }
      }

      if (!accumulatedText) {
        setChatMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = { sender: "ai", text: t.aiChat.noAnswer };
          return newMessages;
        });
      }
    } catch (error) {
      setChatMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { sender: "ai", text: t.aiChat.error };
        return newMessages;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="d-flex align-items-center justify-content-center"
        style={{
          position: "fixed", bottom: 24, right: 24, width: 56, height: 56,
          borderRadius: "50%", backgroundColor: "#1E88E5", border: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 1050,
        }}
      >
        {isOpen ? <X /> : <MessageCircle />}
      </Button>

      {isOpen && (
        <Card style={{
          position: "fixed", bottom: 90, right: 24, width: 380, height: 500,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 1050,
        }}>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Cpu className="me-2" size={20} />
              {t.aiChat.title}
            </div>
            <Button variant="link" size="sm" onClick={() => setIsOpen(false)}>
              <X size={20} />
            </Button>
          </Card.Header>

          <Card.Body style={{ overflowY: "auto", padding: "1rem" }}>
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`mb-3 ${msg.sender === "user" ? "text-end" : ""}`}
              >
                <div
                  className={`d-inline-block p-2 rounded ${
                    msg.sender === "user"
                      ? "bg-primary text-white"
                      : "bg-light text-dark"
                  }`}
                  style={{ maxWidth: "80%", textAlign: "left" }}
                >
                  {msg.sender === "ai" ? (
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                        code: ({node, inline, ...props}) => (
                          inline 
                            ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                            : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                        ),
                        pre: ({node, ...props}) => <pre style={{margin: '0.5em 0', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto'}} {...props} />,
                        ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                        ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                        li: ({node, ...props}) => <li style={{margin: '0.25em 0'}} {...props} />,
                        blockquote: ({node, ...props}) => <blockquote style={{margin: '0.5em 0', paddingLeft: '1em', borderLeft: '3px solid #dee2e6', color: '#6c757d'}} {...props} />,
                        h1: ({node, ...props}) => <h1 style={{fontSize: '1.5em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                        h2: ({node, ...props}) => <h2 style={{fontSize: '1.3em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                        h3: ({node, ...props}) => <h3 style={{fontSize: '1.1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                        a: ({node, ...props}) => <a style={{color: '#0d6efd'}} {...props} target="_blank" rel="noopener noreferrer" />,
                        table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', margin: '0.5em 0', width: '100%'}} {...props} />,
                        th: ({node, ...props}) => <th style={{border: '1px solid #dee2e6', padding: '0.5em', backgroundColor: '#f8f9fa'}} {...props} />,
                        td: ({node, ...props}) => <td style={{border: '1px solid #dee2e6', padding: '0.5em'}} {...props} />,
                      }}
                    >
                      {msg.text || (loading && idx === chatMessages.length - 1 ? "..." : "")}
                    </ReactMarkdown>
                  ) : (
                    msg.text || (loading && idx === chatMessages.length - 1 ? "..." : "")
                  )}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </Card.Body>

          <Card.Footer>
            <Form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={1}
                  placeholder={t.aiChat.questionPlaceholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  style={{ borderRadius: "20px 0 0 20px", resize: "none" }}
                />
                <Button type="submit" disabled={loading || !input.trim()} variant="primary">
                  <Send size={18} />
                </Button>
              </InputGroup>
            </Form>
          </Card.Footer>
        </Card>
      )}
    </>
  );
}
